import { useState, useEffect, useCallback } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function UnzonedAudit() {
  const [rows, setRows] = useState([]);
  const [zones, setZones] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [zone, setZone] = useState("");
  const load = useCallback(() => api.get("/mz/unzoned").then((r) => setRows(r.data.items)), []);
  useEffect(() => { load(); api.get("/mz/zone-options?include_inactive=false").then((r) => setZones(r.data.items)); }, [load]);
  const key = (r) => `${r.collection}:${r.id}`;
  const assign = async () => {
    const items = rows.filter((r) => sel.has(key(r))).map((r) => ({ collection: r.collection, id: r.id }));
    try { const { data } = await api.post("/mz/unzoned/assign", { items, zone_id: zone }); toast.success(`${data.updated} record(s) assigned`); setSel(new Set()); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <div className="cc-card p-4" data-testid="unzoned-audit">
      <h3 className="font-display font-semibold text-slate-800">Records without a zone</h3>
      <p className="text-xs text-slate-400 mt-0.5">Until a zone is set, these stay visible to salespeople assigned to any zone in the same market, so nobody loses access. Assign zones here to complete strict zone access.</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Select value={zone} onValueChange={setZone}><SelectTrigger className="h-8 w-64" data-testid="unzoned-zone"><SelectValue placeholder="Choose zone…" /></SelectTrigger>
          <SelectContent>{zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.market} › {z.name}</SelectItem>)}</SelectContent></Select>
        <Button size="sm" disabled={!zone || !sel.size} onClick={assign} data-testid="unzoned-assign">Assign {sel.size || ""} selected</Button>
      </div>
      {rows.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center" data-testid="unzoned-empty">Every active record has a zone.</p> : (
        <table className="mt-3 w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400"><th className="w-8"></th><th>Record</th><th>Type</th><th>Market</th><th>Old zone text</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={key(r)} className="border-t border-slate-100">
              <td className="py-1.5"><Checkbox checked={sel.has(key(r))} onCheckedChange={(v) => { const n = new Set(sel); v ? n.add(key(r)) : n.delete(key(r)); setSel(n); }} /></td>
              <td>{r.label || "—"}</td><td className="text-slate-500">{r.collection}</td><td>{r.territory || "—"}</td><td className="text-slate-400">{r.legacy_zone || "—"}</td>
            </tr>))}</tbody>
        </table>
      )}
    </div>
  );
}
